import {
  createRootRouteWithContext,
  Outlet,
  redirect,
  useRouterState,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { Suspense } from 'react';
import { LOGIN_PATH, readAuthToken } from '@noor/api';
import { env } from '@noor/config';
import { SessionServices } from '@/app/providers/session-services';
import {
  accessQueries,
  canAccessRoutes,
  createPermissionChecker,
} from '@/entities/access';
import { AppShellLayout } from '@/widgets/app-shell';

/** Router context: the query client is injected so loaders can prefetch. */
export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ location, matches, context }) => {
    if (location.pathname === LOGIN_PATH) return;
    // Signed out: the matched route's own guard redirects to sign-in.
    if (!env.VITE_ENABLE_MSW && !readAuthToken()) return;

    // Load the grants before anything renders (so `useCan` never flashes),
    // then enforce every matched route's `staticData.permission`.
    const { permissions } = await context.queryClient.ensureQueryData(
      accessQueries.mine(),
    );
    const can = createPermissionChecker(permissions);
    const branch = matches.map((match) => match.staticData);
    if (!canAccessRoutes(branch, can)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/forbidden', replace: true });
    }
  },
  component: RootLayout,
});

function RootLayout() {
  // The sign-in screen is a public, full-bleed page — render it WITHOUT the app
  // shell. Every other route gets the authenticated chrome (icon rail + main).
  const isAuthScreen = useRouterState({
    select: (s) => s.location.pathname === LOGIN_PATH,
  });

  if (isAuthScreen) {
    return (
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
    );
  }

  return (
    <AppShellLayout>
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
      <SessionServices />
    </AppShellLayout>
  );
}
